import type { CommnuityInput } from "../../types/community.types";
import { apiClient } from "../axios.config";
import type {
  ArticleListRequest,
  CommunityArticleLikesRequest,
  CommunityCommentRequest,
  CommunityReportRequest,
  NoticeListRequest,
} from "../dto/request/community.request";
import type { Response } from "../dto/response/common.response";
import type {
  CommunityArticleDetailResponse,
  CommunityArticleResponse,
  CommunityCommentResponse,
  NoticeDetailResponse,
  NoticeListResponse,
} from "../dto/response/community.response";

const model = "/v1/community";

/**
 * Community Service API
 * @module CommunityService
 * @description 커뮤니티 관련 API
 * @author DongGyun Yoo
 * @since 1.0.0
 */

export async function communityArticleList(
  form: ArticleListRequest,
): Promise<Response<{ articleList: CommunityArticleResponse[] }>> {
  const { data } = await apiClient.get(`${model}/article/list`, {
    params: {
      ...form,
      golfParkCodeList: form.golfParkCodeList?.join(","),
    },
  });
  return data;
}

export async function communityArticleLikes(
  form: CommunityArticleLikesRequest,
): Promise<Response> {
  const { data } = await apiClient.put(`${model}/article/likes`, form);
  return data;
}

export async function communityArticleWrite(
  form: CommnuityInput & { golfParkCode: string | number },
  files: File[],
): Promise<Response> {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("fileList", file));
  formData.append(
    "data",
    new Blob([JSON.stringify(form)], { type: "application/json" }),
  );

  const { data } = await apiClient.post(`${model}/article/write`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
}

export async function communityArticleEdit(
  form: CommnuityInput & {
    golfParkCode: string | number;
    articleId: string | number;
  },
  files: File[],
): Promise<Response> {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("fileList", file));
  formData.append(
    "data",
    new Blob([JSON.stringify(form)], { type: "application/json" }),
  );

  const { data } = await apiClient.put(
    `${model}/article/${form.articleId}/update`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
}

export async function communityArticleDetail(
  id: string | number,
): Promise<Response<CommunityArticleDetailResponse>> {
  const { data } = await apiClient.get(`${model}/article/${id}/detail`);
  return data;
}

export async function communityArticleDelete(
  id: string | number,
): Promise<Response> {
  const { data } = await apiClient.delete(`${model}/article/${id}/delete`);
  return data;
}

export async function communityArticleComment(
  id: string | number,
  form?: CommunityCommentRequest,
): Promise<Response<{ commentList: CommunityCommentResponse[] }>> {
  const { data } = await apiClient.get(`${model}/article/${id}/comment/list`, {
    params: form,
  });
  return data;
}

export async function communityArticleCommentWrite(
  id: string | number,
  content: string,
): Promise<Response> {
  const { data } = await apiClient.post(
    `${model}/article/${id}/comment/write`,
    { content },
  );
  return data;
}

export async function communityArticleCommentDelete(
  id: string | number,
  commentId: string | number,
): Promise<Response> {
  const { data } = await apiClient.delete(
    `${model}/article/${id}/comment/${commentId}/delete`,
  );
  return data;
}

export async function communityReport(
  form: CommunityReportRequest,
): Promise<Response> {
  const { data } = await apiClient.post(`${model}/report/save`, form);

  return data;
}

/** 공지사항 목록 조회 */
export async function noticeList(
  form?: NoticeListRequest,
): Promise<Response<NoticeListResponse>> {
  const { data } = await apiClient.get(`${model}/notice/list`, {
    params: form,
  });
  return data;
}

/** 공지사항 상세 조회 */
export async function noticeDetail(
  id: string | number,
): Promise<Response<NoticeDetailResponse>> {
  const { data } = await apiClient.get(`${model}/notice/${id}/detail`);
  return data;
}
